import type { LDClient, LDContext } from 'launchdarkly-js-client-sdk';

import { logWarning } from '../rollbar';

const heldContexts = new WeakMap<
  LDClient,
  { count: number; pendingContext?: LDContext }
>();

// Authentication may finish before the signup promise resolves. Defer that
// identity change until conversion events have used the original exposure key.
export async function identifyLDContext(client: LDClient, context: LDContext) {
  const held = heldContexts.get(client);
  if (held) {
    held.pendingContext = context;
    return;
  }
  await client.identify(context);
}

export function holdSignupContext(client?: LDClient) {
  if (!client) return () => {};
  const held = heldContexts.get(client) ?? { count: 0 };
  held.count += 1;
  heldContexts.set(client, held);
  let released = false;

  return () => {
    if (released) return;
    released = true;
    held.count -= 1;
    if (held.count) return;
    heldContexts.delete(client);
    if (held.pendingContext) {
      void identifyLDContext(client, held.pendingContext).catch((error) => {
        logWarning('LaunchDarkly signup identity update failed', { error });
      });
    }
  };
}

export async function trackParentSignupCompleted(
  client: LDClient | undefined,
  hasPhone: boolean
) {
  if (!client) return;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    // No signup fields or contact details are included in analytics events.
    client.track('parent-signup-completed');
    if (hasPhone) client.track('parent-signup-completed-with-phone');
    // Give the SDK a chance to send before navigation, without letting an
    // unavailable analytics service keep a successful signup on the form.
    await Promise.race([
      client.flush(),
      new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, 1000);
      }),
    ]);
  } catch (error) {
    logWarning('LaunchDarkly signup conversion tracking failed', { error });
  } finally {
    clearTimeout(timeout);
  }
}
